const RATE_PERCENTAGE = 100;
const MONTHS_IN_A_YEAR = 12;
const INTEREST_RATE_FACTOR_MONTHLY = RATE_PERCENTAGE * MONTHS_IN_A_YEAR;

/**
 * Calculation for Principal & Interest monthly repayment
 *
 * @param loanAmount Should be a whole number
 * @param interestRate Expect up to 2 decimal points, trailing decimal points will be rounded
 * @param loanTerm Should be a whole number in years
 * @returns A whole number rounded up
 */
export function calculatePrincipalInterestMonthlyRepayment(
  loanAmount: number,
  interestRate: number,
  loanTerm: number
): number {
  // Early exit if any values are less than or equal to 0
  if (loanAmount <= 0 || loanTerm <= 0 || interestRate <= 0) return 0;

  // 12 months in a year, it will be divided to be factored to 12 months basis of interest rate
  const calculatedInterestRate = interestRate / INTEREST_RATE_FACTOR_MONTHLY;
  const loanTermInMonths = loanTerm * MONTHS_IN_A_YEAR;
  const amount = (1 + calculatedInterestRate) ** loanTermInMonths;
  const monthlyRepayment = (loanAmount * (calculatedInterestRate * amount)) / (amount - 1);
  return Number.isNaN(monthlyRepayment) ? 0 : Math.round(monthlyRepayment);
}

/**
 * Calculation for Interest only monthly repayment
 *
 * @param loanAmount Should be a whole number
 * @param interestRate Expect up to 2 decimal points, trailing decimal points will be rounded
 * @returns A whole number rounded up
 */
export function calculateInterestOnlyMonthlyRepayment(loanAmount: number, interestRate: number): number {
  // Early exit if any values are less than or equal to 0
  if (loanAmount <= 0 || interestRate <= 0) return 0;

  const calculatedInterestRate = interestRate / RATE_PERCENTAGE;
  const monthlyRepayment = (loanAmount * calculatedInterestRate) / MONTHS_IN_A_YEAR;
  return Math.round(monthlyRepayment);
}

/**
 * Calculation for the equity value
 *
 * @param propertyValue Should be a whole number
 * @returns A whole number rounded up
 */
export function calculateEquityValue(propertyValue: number): number {
  return Math.round(propertyValue * 0.8);
}

/**
 * Calculate accessible equity based on equity value and outstanding loan amount
 *
 * @param outstandingLoanAmount Should be a whole number
 * @param equityValue Should be a whole number
 * @returns A whole number rounded up
 */
export function calculateAccessibleEquityValue(outstandingLoanAmount: number, equityValue: number): number {
  const accessibleEquity = equityValue - outstandingLoanAmount;
  return accessibleEquity > 0 ? accessibleEquity : 0;
}
